'use client';

import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import CustomerApiService, { type DashboardData } from '@/services/CustomerApiService';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

interface CustomerDashboardContextValue {
  data: DashboardData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CustomerDashboardContext = createContext<CustomerDashboardContextValue | undefined>(undefined);

export function CustomerDashboardProvider({ children }: { children: React.ReactNode }) {
  const { customer, isLoading: authLoading } = useCustomerAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedForCustomerId = useRef<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!customer?.id) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const dashboardData = await CustomerApiService.getDashboard();
      setData(dashboardData);
      fetchedForCustomerId.current = customer.id;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (authLoading) return;
    if (!customer?.id) {
      setData(null);
      fetchedForCustomerId.current = null;
      return;
    }
    if (fetchedForCustomerId.current === customer.id) {
      return;
    }
    void fetchData();
  }, [authLoading, customer?.id, fetchData]);

  const refetch = useCallback(async () => {
    fetchedForCustomerId.current = null;
    await fetchData();
  }, [fetchData]);

  const value: CustomerDashboardContextValue = {
    data,
    isLoading: authLoading || isLoading,
    error,
    refetch,
  };

  return (
    <CustomerDashboardContext.Provider value={value}>{children}</CustomerDashboardContext.Provider>
  );
}

export function useCustomerDashboard(): CustomerDashboardContextValue {
  const ctx = useContext(CustomerDashboardContext);
  if (ctx) return ctx;

  return {
    data: null,
    isLoading: true,
    error: null,
    refetch: async () => {},
  };
}
